import { html, View } from 'rune-ts';
import klass from './FundingCard.module.scss';
import { createProgressParts } from '../../../../../../shared/rune-component/Progress';
import { ButtonAction } from '@services/MarppleShop/shared/components/atoms/ButtonAction/ButtonAction.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { doIf } from '@services/MarppleShop/shared/util';
import { format } from 'date-fns';
import { preventEscape } from '@services/MarppleShop/shared/util/preventEscape.ts';
import { HumanReadableFormatter } from '../../../../../../shared/utils/formatter/text/human-readable-text-formatter.ts';
import {
  FundingHelper,
  FundingStatus,
  FundingStatuses,
} from '@services/MarppleShop/renderApp/components/cells/FundingCard/FundingHelper.ts';
import { Localized } from '../../../../../../shared/type';
import { popToastMsg } from '@modules/MShop/Util/F/Function/toast';
import { makeMainUrl } from '@services/MarppleShop/shared/util/url.ts';
import { clipboard } from '../../../../../../shared/utils/Front/clipboard.ts';
import { ProductBadgeList } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/ProductBadgeList.ts';
import { ProductBadgeVariant } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/strategies';
import { ProductBadgeSize } from '@services/MarppleShop/features/ProductBadge/type';
import {
  ProductTimeHelper,
  ResultDiffDate,
} from '@services/MarppleShop/renderApp/components/cells/ProductCard/utils/ProductTimeHelper.ts';
import { ProductThumbnailView } from '@services/MarppleShop/renderApp/components/cells/ProductThumbnail/ProductThumbnailView.ts';
import { ProductThumbnail } from '@services/MarppleShop/features/ProductList/type';
import {
  ITimerSubscribable,
  TimerSubscriptionManager,
} from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/TimerSubscriptionManager.ts';
import { TimerTick } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/timer.ts';
import { hydrateSafetyDate } from '../../../../../../shared/utils/Front/hydration.ts';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { makeSubScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';

export interface FundingCardProps {
  name: Localized<string>;
  thumbnail_info: ProductThumbnail;
  description?: string;
  quantity?: number;
  order_count?: number;

  sell_start_at: Date;
  sell_end_at: Date;
  goal_amount: number;
  total_amount: number;

  is_membership?: boolean;
  out_of_stock?: boolean;
  is_public?: boolean;
  is_quantity_public?: boolean;
  is_mobile?: boolean;

  domain_name?: string;
  product_id?: number;
  server_timestamp: number;

  /**
   * 이동 이벤트는 카드에서 관리
   */
  navigate?: boolean;
}

export class FundingCard extends View<FundingCardProps> implements ITimerSubscribable {
  private readonly shareButtonView: ButtonAction;

  private readonly badgesView: ProductBadgeList;

  private readonly timerSubscription = new TimerSubscriptionManager(this);

  constructor(data: FundingCardProps) {
    super(hydrateSafetyDate(data, ['sell_start_at', 'sell_end_at']));
    const {
      is_mobile,
      is_membership,
      is_quantity_public,
      is_public,
      out_of_stock,
      sell_start_at = new Date(),
      sell_end_at = new Date(),
      quantity = 0,
      order_count = 0,
    } = this.data;

    this.shareButtonView = new ButtonAction({
      klass: klass.cta,
      size: this.data.is_mobile ? 'xSmall' : 'small',
      label: html`
        <div class="${klass.label}">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none">
            <path
              d="M14.8537 7.35414L9.85371 12.3541C9.78378 12.4241 9.69466 12.4718 9.59762 12.4912C9.50057 12.5105 9.39998 12.5006 9.30857 12.4627C9.21716 12.4248 9.13904 12.3607 9.0841 12.2784C9.02917 12.1961 8.99988 12.0993 8.99996 12.0004V9.51477C5.43121 9.71727 2.98434 12.0316 2.29746 12.7648C2.18961 12.88 2.04817 12.9582 1.89328 12.9883C1.73838 13.0185 1.57793 12.999 1.43475 12.9327C1.29157 12.8663 1.17295 12.7565 1.09579 12.6189C1.01863 12.4812 0.986842 12.3228 1.00496 12.166C1.23684 10.1498 2.34121 8.21039 4.11496 6.70539C5.58809 5.45539 7.37871 4.66227 8.99996 4.52352V2.00039C8.99988 1.90145 9.02917 1.8047 9.0841 1.7224C9.13904 1.64011 9.21716 1.57596 9.30857 1.53808C9.39998 1.50021 9.50057 1.4903 9.59762 1.50963C9.69466 1.52895 9.78378 1.57664 9.85371 1.64664L14.8537 6.64664C14.9002 6.69308 14.9371 6.74822 14.9622 6.80892C14.9874 6.86962 15.0004 6.93469 15.0004 7.00039C15.0004 7.0661 14.9874 7.13117 14.9622 7.19186C14.9371 7.25256 14.9002 7.30771 14.8537 7.35414Z"
              fill="#A2A2A2"
            />
          </svg>
          <span class="${font('14_regular', '12_regular')}">${ET('mps2::funding::card::share')}</span>
        </div>
      `,
    });

    this.badgesView = ProductBadgeList.forList(
      ProductBadgeVariant.FUNDING_BADGE,
      {
        is_funding: true,
        is_quantity_public: Boolean(is_quantity_public),
        is_public: Boolean(is_public),
        is_membership: Boolean(is_membership),
        out_of_stock: Boolean(out_of_stock),

        sell_start_at,
        sell_end_at,
        quantity,
        order_count,

        is_trending: false,
        is_popular: false,
        is_token_gate: false,

        server_timestamp: this.data.server_timestamp,
      },
      {
        styles: {
          container: klass.badge,
        },
        is_lazy_timer: true,
        size: is_mobile ? ProductBadgeSize.small : ProductBadgeSize.medium,
        max_length: 2,
        lang: ET.lang,
      },
    );
  }

  protected override template() {
    const { sell_start_at = new Date(), navigate = true } = this.data;
    const status = this.getFundingStatus();
    const { bar, percentage } = createProgressParts({
      start: 0,
      end: this.getFundingGoalPercentage(),
      auto: false,
      klass: this.getFundingStatus() === FundingStatus.FAILED ? klass.progress_failed : undefined,
    });

    return html`
      <a
        ${navigate
          ? html`href="${this.makeFundingUrl()}"
            data-post-message="${dataStr(makeSubScreenNavigate(this.makeFundingUrl()))}"`
          : html``}
        class="${klass.card}"
      >
        <!-- 펀딩 상품 썸네일 -->
        <div class="${klass.left}">
          ${new ProductThumbnailView({
            thumbnail: this.data.thumbnail_info,
            options: {
              resize_width: 350,
            },
          })}
          ${this.badgesView}
        </div>

        <!-- 펀딩 상품 정보 -->
        <div class="${klass.right}">
          <div class="${klass.head}">
            <h3 class="${klass.title} ${font('16_bold', '14_medium')}">
              ${preventEscape(this.data.name.kr)}
            </h3>
            <p class="${klass.description} ${font('14_regular', '12_regular')}">
              ${this.data.description ? preventEscape(this.data.description) : ''}
            </p>
            ${doIf(
              () =>
                html`<span class="${klass.coming_soon} ${font('14_bold', '12_bold')}"
                  >${preventEscape(
                    ET('mps2::funding::card::coming_soon', {
                      date: preventEscape(
                        `${format(new Date(sell_start_at), `MM/dd`)}(${HumanReadableFormatter.humanizeToDate({ date: sell_start_at, day_format: 'short' })})`,
                      ),
                    }),
                  )}</span
                >`,
              status === FundingStatus.COMING_SOON,
            )}
          </div>

          ${doIf(
            () => html`
              <div class="${klass.body}">
                <div class="${klass.status}">
                  <div class="${klass.status_left}">
                    ${percentage} ${doIf(() => this.makeFundingGoalAmountHtml(), !this.data.is_mobile)}
                  </div>

                  ${doIf(
                    () => html` <div class="${klass.status_right}">${this.makeFundingStatusHtml()}</div> `,
                    !!this.data.sell_end_at,
                  )}
                </div>

                <div class="${klass.bar}">${bar}</div>
              </div>
            `,
            status !== FundingStatus.COMING_SOON,
          )}

          <div class="${klass.footer}">${this.shareButtonView}</div>
        </div>
      </a>
    `;
  }

  protected override onRender() {
    const status = this.getFundingStatus();

    this.bindEvent();

    // 타이머 구독 시작
    if (!(status === FundingStatus.SUCCESS || status === FundingStatus.FAILED)) {
      this.timerSubscription.subscribe();
    }
  }

  protected override onUnmount(): void {
    super.onUnmount();
    this.destroy();
  }

  private getFundingStatus(): FundingStatuses {
    return FundingHelper.status({
      ...this.data,
      quantity: this.data.quantity || 0,
      order_count: this.data.order_count || 0,
      out_of_stock: Boolean(this.data.out_of_stock),
    });
  }

  private getFundingGoalPercentage() {
    return FundingHelper.percentage(this.data);
  }

  private makeFundingGoalAmountHtml() {
    const { amount, symbol } = HumanReadableFormatter.humanizeToCurrency({
      amount: this.data.total_amount,
      lang: ET.lang,
    });

    if (ET.lang === 'en')
      return html`<span class="${klass.status_amount} ${font('14_regular')}"
        >${symbol}${amount.toLocaleString('en-US')}</span
      >`;
    if (ET.lang === 'jp')
      return html`<span class="${klass.status_amount} ${font('14_regular')}"
        >${symbol}${amount.toLocaleString('ja-JP')}</span
      >`;
    return html`<span class="${klass.status_amount} ${font('14_regular')}"
      >${amount.toLocaleString('ko-KR')}${symbol}</span
    >`;
  }

  private makeFundingStatusHtml() {
    const status = this.getFundingStatus();
    if (status === FundingStatus.SUCCESS)
      return html`<span class="${klass.status_success} ${font('14_bold', '12_bold')}"
        >${ET('mps2::funding::card::status::success')}</span
      >`;
    if (status === FundingStatus.FAILED)
      return html`<span class="${klass.status_failed} ${font('14_bold', '12_bold')}"
        >${ET('mps2::funding::card::status::failed')}</span
      >`;
    if (status === FundingStatus.ING || status === FundingStatus.BREAKTHROUGH)
      return html`<span class="${klass.status_ing} ${font('14_bold', '12_bold')}">
        ${this.makeFundingIntervalText(
          ProductTimeHelper.diffDate(
            this.data.sell_end_at || new Date(),
            this.renderCount ? new Date() : new Date(this.data.server_timestamp),
          ),
        )}
      </span>`;

    return '';
  }

  private makeFundingIntervalText({ day, hour, minute, second }: ResultDiffDate) {
    if (day) return ET('mps2::funding::card::status::ing_day', { day });
    if (hour) return ET('mps2::funding::card::status::ing_hour', { hours: hour });
    if (minute) return ET('mps2::funding::card::status::ing_minute', { minutes: minute });
    if (second) return ET('mps2::funding::card::status::ing_second', { seconds: second });
    return ET('mps2::funding::card::status::ing_second', { seconds: 0 });
  }

  private makeFundingUrl() {
    return makeMainUrl(`/${ET.lang}/${this.data.domain_name}/products/${this.data.product_id}`);
  }

  private update(now: Date) {
    requestAnimationFrame(() =>
      setTimeout(() => {
        this.data.sell_start_at = now;
        this.redraw();
        this.bindEvent();
        this.timerSubscription.resubscribe();
      }, 3000),
    );
  }

  private bindEvent() {
    this.shareButtonView.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();

      clipboard(this.makeFundingUrl(), this.data.is_mobile).then(() => {
        if (!MShopUtilF.isApp()) popToastMsg(ET('mps2::funding::card::toast::success'), 'info');
      });
    });
  }

  onTimerTick(event: TimerTick) {
    const { timestamp } = event.detail;
    const status = this.getFundingStatus();
    const now = new Date(timestamp);

    const { day, hour, minute, second } = ProductTimeHelper.diffDate(
      status === FundingStatus.COMING_SOON ? this.data.sell_start_at : this.data.sell_end_at,
      now,
    );

    /**
     * 출시 예정 -> 진행 중 -> 종료
     */

    // 출시 예정 -> 진행 중
    if (status === FundingStatus.COMING_SOON && !(day || hour || minute) && second <= 1) {
      this.update(now);
      return;
    }

    const $interval = this.element().querySelector(`.${klass.status_ing}`);

    if ($interval) {
      $interval.textContent = this.makeFundingIntervalText({
        day,
        hour,
        minute,
        second,
      });
    }

    // 진행 중 -> 종료
    if (
      (status === FundingStatus.ING || status === FundingStatus.BREAKTHROUGH) &&
      !(day || hour || minute || second)
    ) {
      this.update(now);
      return;
    }

    // 종료
    if (status === FundingStatus.FAILED || status === FundingStatus.SUCCESS) {
      this.redraw();
      this.destroy();
    }
  }

  destroy(): void {
    this.badgesView.destroy();
    this.timerSubscription.unsubscribe();
  }
}
